import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { computePL, fmt, pct } from '../utils';
import { computeSOWCosts, computeRoomCosts } from '../computeSOWCosts';

// ── Helpers ───────────────────────────────────────────────────────────────────

const BRAND     = '#2493b5';
const BRAND_DARK= '#1a7a97';
const GREEN     = '#39b54a';
const GRAY      = '#939598';
const LIGHT_BG  = '#f4f6f9';
const BORDER    = '#dde1e7';

function label(val) {
  if (!val) return '—';
  return String(val).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

const ROOM_TYPE_LABELS = {
  master_bedroom: 'Master Bedroom', bedroom: 'Bedroom',
  master_bath: 'Master Bath',       bathroom: 'Bathroom',
  kitchen: 'Kitchen',               living: 'Living Room',
  dining: 'Dining Room',            laundry: 'Laundry',
  hallway: 'Hallway',               garage: 'Garage', other: 'Other',
};

// ── Sub-components ────────────────────────────────────────────────────────────

function SectionHead({ title, color = BRAND }) {
  return (
    <div style={{
      background: color, color: '#fff',
      padding: '6px 14px', borderRadius: 4,
      fontWeight: 700, fontSize: 13, letterSpacing: '0.03em',
      marginBottom: 8, marginTop: 18,
    }}>
      {title}
    </div>
  );
}

function TwoCol({ rows }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 24px', marginBottom: 8 }}>
      {rows.map(([k, v], i) => v && v !== '—' && (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: `1px solid ${BORDER}`, fontSize: 12 }}>
          <span style={{ color: GRAY }}>{k}</span>
          <span style={{ fontWeight: 500 }}>{v}</span>
        </div>
      ))}
    </div>
  );
}

function CostTable({ rows, showActual = false }) {
  if (!rows || rows.length === 0) return null;
  const total = rows.reduce((s, r) => s + (r.budgeted || r.estimated || 0), 0);
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 4 }}>
      <thead>
        <tr style={{ background: LIGHT_BG }}>
          <th style={{ textAlign: 'left',  padding: '4px 8px', fontWeight: 600, color: GRAY, borderBottom: `1px solid ${BORDER}` }}>Item</th>
          <th style={{ textAlign: 'right', padding: '4px 8px', fontWeight: 600, color: GRAY, borderBottom: `1px solid ${BORDER}` }}>Estimated</th>
          <th style={{ textAlign: 'right', padding: '4px 8px', fontWeight: 600, color: GRAY, borderBottom: `1px solid ${BORDER}` }}>Budgeted</th>
          {showActual && <th style={{ textAlign: 'right', padding: '4px 8px', fontWeight: 600, color: GRAY, borderBottom: `1px solid ${BORDER}` }}>Actual</th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} style={{ borderBottom: `1px solid ${BORDER}` }}>
            <td style={{ padding: '4px 8px' }}>{r.label}</td>
            <td style={{ padding: '4px 8px', textAlign: 'right', color: GRAY }}>{fmt(r.estimated)}</td>
            <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 500 }}>{fmt(r.budgeted || r.estimated)}</td>
            {showActual && <td style={{ padding: '4px 8px', textAlign: 'right', color: r.actual > 0 ? '#333' : GRAY }}>{r.actual > 0 ? fmt(r.actual) : '—'}</td>}
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr style={{ background: LIGHT_BG, fontWeight: 700 }}>
          <td style={{ padding: '5px 8px', borderTop: `2px solid ${BORDER}` }}>Section Total</td>
          <td style={{ padding: '5px 8px', textAlign: 'right', borderTop: `2px solid ${BORDER}`, color: GRAY }}></td>
          <td style={{ padding: '5px 8px', textAlign: 'right', borderTop: `2px solid ${BORDER}`, color: BRAND }}>{fmt(total)}</td>
          {showActual && <td style={{ borderTop: `2px solid ${BORDER}` }} />}
        </tr>
      </tfoot>
    </table>
  );
}

// ── Main Report ───────────────────────────────────────────────────────────────

export default function SOWReport() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode') || 'full'; // 'full' | 'sow'
  const sowOnly = mode === 'sow';

  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    api.getDeal(id).then(d => { setDeal(d); setLoading(false); });
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Auto-trigger print dialog after data loads
  useEffect(() => {
    if (!loading && deal) {
      const t = setTimeout(() => window.print(), 600);
      return () => clearTimeout(t);
    }
  }, [loading, deal]);

  if (loading) return (
    <div style={{ padding: 60, textAlign: 'center', fontFamily: 'Inter, sans-serif', color: GRAY }}>
      Preparing report…
    </div>
  );

  if (!deal) return (
    <div style={{ padding: 60, textAlign: 'center', fontFamily: 'Inter, sans-serif' }}>Deal not found.</div>
  );

  const pl       = computePL(deal);
  const sowLines = computeSOWCosts(deal);
  const sowDb    = deal.sow_costs || [];
  const rooms    = deal.rooms || [];
  const sys      = deal.sow_systems   || {};
  const demo     = deal.sow_demo      || {};
  const ext      = deal.sow_exterior  || {};
  const land     = deal.sow_landscape || {};

  // Merge computed lines with DB budgeted values
  const dbMap = {};
  sowDb.forEach(r => { dbMap[`${r.section}|||${r.label}`] = r; });

  function getSowRows(sectionPrefix) {
    return sowLines
      .filter(l => l.section === sectionPrefix || l.section.startsWith(sectionPrefix))
      .map(l => {
        const db = dbMap[`${l.section}|||${l.label}`];
        return { label: l.label, estimated: l.estimated, budgeted: db?.budgeted ?? l.estimated, actual: db?.actual || 0 };
      });
  }

  function getRoomRows(roomId) {
    const lines = computeRoomCosts(rooms.find(r => r.id === roomId) || {});
    return lines.map(l => {
      const db = dbMap[`room_${roomId}|||${l.label}`];
      return { label: l.label, estimated: l.amount, budgeted: db?.budgeted ?? l.amount, actual: db?.actual || 0 };
    });
  }

  // Compute grand total across all sow sections
  const grandTotal = (() => {
    const computedMap = {};
    sowLines.forEach(l => { computedMap[`${l.section}|||${l.label}`] = l.estimated; });
    const allKeys = new Set([...Object.keys(computedMap), ...Object.keys(dbMap)]);
    let total = 0;
    for (const key of allKeys) {
      const db = dbMap[key];
      total += db ? (db.budgeted ?? db.estimated ?? 0) : (computedMap[key] || 0);
    }
    return total;
  })();

  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  // Section group helpers
  const demoRows = getSowRows('demo');
  const roofRows = getSowRows('roof');
  const plumbRows = getSowRows('plumbing');
  const elecRows  = getSowRows('electrical');
  const hvacRows  = getSowRows('hvac');
  const foundRows = getSowRows('foundation');
  const systemRows = [...roofRows, ...plumbRows, ...elecRows, ...hvacRows, ...foundRows];

  const extWinRows   = getSowRows('exterior_windows');
  const extSidRows   = getSowRows('exterior_siding');
  const extPaintRows = getSowRows('exterior_paint');
  const extGutRows   = getSowRows('exterior_gutters');
  const extPwRows    = getSowRows('exterior_pressure_wash');
  const exteriorRows = [...extWinRows, ...extSidRows, ...extPaintRows, ...extGutRows, ...extPwRows];

  const landRows     = getSowRows('landscape');
  const fenceRows    = getSowRows('fence');
  const drivewayRows = getSowRows('driveway');
  const landscapeRows = [...landRows, ...fenceRows, ...drivewayRows];

  // Materials list (reuse logic from MaterialsSection)
  const materials = buildMaterialsList(deal);
  const materialsTotal = materials.flatMap(g => g.items).reduce((s, i) => s + (i.cost || 0), 0);

  return (
    <div style={{ fontFamily: 'Inter, Arial, sans-serif', fontSize: 12, color: '#1e1e1e', maxWidth: 850, margin: '0 auto', padding: '20px 28px' }}>

      {/* ── Print button (hidden when printing) ── */}
      <div className="no-print" style={{ textAlign: 'right', marginBottom: 16 }}>
        <button onClick={() => window.print()}
          style={{ background: BRAND, color: '#fff', border: 'none', borderRadius: 6,
            padding: '10px 22px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
          🖨 Print / Save as PDF
        </button>
      </div>

      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, paddingBottom: 14, borderBottom: `3px solid ${BRAND}` }}>
        <div>
          <img src="/logo-white.png" alt="MFMO" style={{ height: 40, display: 'none' }} onError={() => {}} />
          <div style={{ fontSize: 22, fontWeight: 800, color: BRAND, letterSpacing: '-0.02em' }}>My Fair Market Offer</div>
          <div style={{ fontSize: 11, color: GRAY, marginTop: 1 }}>
            {sowOnly ? 'Scope of Work & Budget Estimate' : 'Rehab Estimator · Full Deal Analysis'}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 11, color: GRAY }}>Generated {today}</div>
          <div style={{ fontSize: 11, color: GRAY, marginTop: 2 }}>{deal.status}</div>
        </div>
      </div>

      {/* ── Deal Summary ── */}
      <div style={{ background: BRAND, color: '#fff', borderRadius: 6, padding: '14px 18px', marginBottom: 20 }}>
        <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 6 }}>{deal.address || 'Address not set'}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
          {(sowOnly ? [
            ['Bedrooms',     deal.bedrooms ? `${deal.bedrooms} bd / ${deal.bathrooms} ba` : '—'],
            ['Sq Footage',   deal.sq_footage ? `${deal.sq_footage} sqft` : '—'],
            ['Rehab Budget', fmt(pl.rehabBase)],
            ['Contingency',  fmt(pl.contingency)],
            ['Total Rehab',  fmt(pl.rehabTotal)],
            ['Hold Period',  deal.hold_months ? `${deal.hold_months} months` : '—'],
          ] : [
            ['ARV',            fmt(pl.arv)],
            ['Purchase Price', fmt(pl.purchasePrice)],
            ['Rehab Budget',   fmt(pl.rehabBase)],
            ['Max Offer (MAO)',fmt(pl.mao)],
            ['Net Proceeds',   fmt(pl.netBudgeted)],
            ['Profit % of ARV',pct(pl.profitPctBudgeted)],
            ['ROI',            pct(pl.roiBudgeted)],
            ['Hold Period',    deal.hold_months ? `${deal.hold_months} months` : '—'],
          ]).map(([k, v]) => (
            <div key={k}>
              <div style={{ fontSize: 10, opacity: 0.8 }}>{k}</div>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{v}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── DEMO & DISPOSAL ── */}
      {(demoRows.length > 0 || demo.notes) && (
        <>
          <SectionHead title="Demo &amp; Disposal" color="#7f8c8d" />
          <TwoCol rows={[
            ['Dumpsters',   demo.dumpster_count > 0 ? `${demo.dumpster_count}× ${demo.dumpster_size || '20yd'}` : null],
            ['Full Cleanout', demo.full_cleanout ? 'Yes' : null],
            ['Haul Away',    demo.haul_away     ? 'Yes' : null],
            ['Demo Labor',   demo.demo_labor > 0 ? fmt(demo.demo_labor) : null],
          ]} />
          {demo.notes && <p style={{ fontSize: 11, color: GRAY, margin: '4px 0 8px' }}>{demo.notes}</p>}
          <CostTable rows={demoRows} showActual />
        </>
      )}

      {/* ── MAJOR SYSTEMS ── */}
      {systemRows.length > 0 && (
        <>
          <SectionHead title="Major Systems" color="#2c3e50" />
          <TwoCol rows={[
            ['Roof',          sys.roof_action && sys.roof_action !== 'none' ? `${label(sys.roof_action)}${sys.roof_sq_ft ? ` — ${sys.roof_sq_ft} sqft` : ''}${sys.roof_type ? ` (${label(sys.roof_type)})` : ''}` : null],
            ['Roof Age',      sys.roof_age ? `${sys.roof_age} yrs` : null],
            ['HVAC',          sys.hvac_action && sys.hvac_action !== 'none' ? `${label(sys.hvac_action)}${sys.hvac_tons ? ` — ${sys.hvac_tons} ton` : ''}` : null],
            ['HVAC Age',      sys.hvac_age ? `${sys.hvac_age} yrs` : null],
            ['Plumbing',      sys.plumbing_action && sys.plumbing_action !== 'none' ? label(sys.plumbing_action) : null],
            ['Water Heater',  sys.wh_action && sys.wh_action !== 'keep' ? `${label(sys.wh_action)}${sys.wh_type ? ` (${label(sys.wh_type)})` : ''}` : null],
            ['Panel',         sys.panel_action && sys.panel_action !== 'none' ? `${label(sys.panel_action)}${sys.panel_amps ? ` — ${sys.panel_amps}A` : ''}` : null],
            ['Wiring',        sys.wiring_type ? `${label(sys.wiring_type)}${sys.wiring_type === 'aluminum' ? (sys.alimicon_present ? ' (alimicons present)' : ' ⚠ alimicons needed') : ''}` : null],
            ['Foundation',    sys.foundation_action && sys.foundation_action !== 'none' ? label(sys.foundation_action) : null],
          ]} />
          <CostTable rows={systemRows} showActual />
        </>
      )}

      {/* ── EXTERIOR ── */}
      {exteriorRows.length > 0 && (
        <>
          <SectionHead title="Exterior" color={GREEN} />
          <TwoCol rows={[
            ['Windows',       ext.window_action && ext.window_action !== 'keep' ? `${label(ext.window_action)}${ext.window_count ? ` — ${ext.window_count} windows` : ''}` : null],
            ['Window Type',   ext.window_type   ? label(ext.window_type) : null],
            ['Siding',        ext.siding_action && ext.siding_action !== 'keep' ? `${label(ext.siding_action)}${ext.siding_sq_ft ? ` — ${ext.siding_sq_ft} sqft` : ''}` : null],
            ['Ext. Paint',    ext.ext_paint_action && ext.ext_paint_action !== 'keep' ? `${label(ext.ext_paint_action)}${ext.ext_paint_sq_ft ? ` — ${ext.ext_paint_sq_ft} sqft` : ''}` : null],
            ['Gutters',       ext.gutters_action && !['keep','none',''].includes(ext.gutters_action) ? `${label(ext.gutters_action)}${ext.gutters_linear_ft ? ` — ${ext.gutters_linear_ft} lft` : ''}` : null],
            ['Pressure Wash', ext.pressure_wash ? `Yes${ext.pressure_wash_sq_ft ? ` — ${ext.pressure_wash_sq_ft} sqft` : ''}` : null],
          ]} />
          <CostTable rows={exteriorRows} showActual />
        </>
      )}

      {/* ── LANDSCAPE ── */}
      {landscapeRows.length > 0 && (
        <>
          <SectionHead title="Landscape &amp; Hardscape" color={GREEN} />
          <TwoCol rows={[
            ['Landscape',  land.landscape_scope ? label(land.landscape_scope) : null],
            ['Sod',        land.sod && land.sod_sq_ft ? `${land.sod_sq_ft} sqft` : null],
            ['Trees',      land.tree_removal_count > 0 ? `Remove ${land.tree_removal_count}` : null],
            ['Fence',      land.fence_action && land.fence_action !== 'none' ? `${label(land.fence_action)} — ${label(land.fence_type || 'wood')}${land.fence_linear_ft ? ` ${land.fence_linear_ft} lft` : ''}` : null],
            ['Driveway',   land.driveway_action && land.driveway_action !== 'keep' ? `${label(land.driveway_action)}${land.driveway_material ? ` — ${label(land.driveway_material)}` : ''}` : null],
          ]} />
          <CostTable rows={landscapeRows} showActual />
        </>
      )}

      {/* ── ROOM-BY-ROOM ── */}
      {rooms.length > 0 && (
        <>
          <SectionHead title="Room-by-Room Scope" color={BRAND} />
          {rooms.map(room => {
            const roomRows = getRoomRows(room.id);
            const roomTotal = roomRows.reduce((s, r) => s + (r.budgeted || r.estimated || 0), 0);
            const sqFt = room.sq_ft || (room.length_ft && room.width_ft ? room.length_ft * room.width_ft : null);
            return (
              <div key={room.id} style={{ marginBottom: 14, pageBreakInside: 'avoid' }}>
                {/* Room header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  background: LIGHT_BG, padding: '5px 10px', borderRadius: 4, borderLeft: `4px solid ${BRAND}`, marginBottom: 4 }}>
                  <div>
                    <span style={{ fontWeight: 700, fontSize: 13 }}>{room.name}</span>
                    <span style={{ fontSize: 11, color: GRAY, marginLeft: 8 }}>{ROOM_TYPE_LABELS[room.room_type] || room.room_type}</span>
                    {sqFt && <span style={{ fontSize: 11, color: GRAY, marginLeft: 8 }}>{sqFt} sqft</span>}
                  </div>
                  {roomTotal > 0 && (
                    <span style={{ fontWeight: 700, color: BRAND, fontSize: 13 }}>{fmt(roomTotal)}</span>
                  )}
                </div>

                {/* Room details */}
                <TwoCol rows={[
                  ['Flooring',      room.flooring_condition !== 'keep' ? `${label(room.flooring_condition)} — ${label(room.flooring_type)}` : null],
                  ['Walls',         room.paint_walls ? 'Paint walls' : null],
                  ['Ceiling',       room.paint_ceiling ? 'Paint ceiling' : null],
                  ['Drywall',       room.drywall_damage && room.drywall_damage !== 'none' ? label(room.drywall_damage) : null],
                  ['Doors',         room.door_count > 0 ? `${room.door_count} doors — ${label(room.door_condition || 'keep')}` : null],
                  ['Trim',          room.trim_condition && room.trim_condition !== 'keep' ? label(room.trim_condition) : null],
                  ['Light Fixtures',room.light_fixtures > 0 ? room.light_fixtures : null],
                  ['Ceiling Fans',  room.ceiling_fans > 0 ? room.ceiling_fans : null],
                  ['AC Vents',      room.ac_vents > 0 ? `${room.ac_vents} — ${label(room.ac_vent_condition || 'keep')}` : null],
                  // Kitchen
                  ['Cabinets',      room.cabinet_condition && room.cabinet_condition !== 'keep' ? label(room.cabinet_condition) : null],
                  ['Countertop',    room.countertop_type ? `${label(room.countertop_type)}${room.countertop_sq_ft ? ` — ${room.countertop_sq_ft} sqft` : ''}` : null],
                  ['Sink',          room.sink_condition && room.sink_condition !== 'keep' ? label(room.sink_condition) : null],
                  ['Fridge',        room.appliance_fridge && room.appliance_fridge !== 'reuse' ? label(room.appliance_fridge) : null],
                  ['Stove',         room.appliance_stove && room.appliance_stove !== 'reuse' ? label(room.appliance_stove) : null],
                  ['Dishwasher',    room.appliance_dishwasher && room.appliance_dishwasher !== 'reuse' ? label(room.appliance_dishwasher) : null],
                  // Bathroom
                  ['Toilet',        room.toilet_condition && room.toilet_condition !== 'keep' ? label(room.toilet_condition) : null],
                  ['Vanity',        room.vanity_condition && room.vanity_condition !== 'keep' ? `${label(room.vanity_condition)} — ${label(room.vanity_size || 'single')}` : null],
                  ['Tub/Shower',    room.tub_shower_condition && room.tub_shower_condition !== 'keep' ? label(room.tub_shower_condition) : null],
                ]} />

                {roomRows.length > 0 && <CostTable rows={roomRows} showActual />}
                {room.notes && <p style={{ fontSize: 11, color: GRAY, margin: '2px 0' }}>Note: {room.notes}</p>}
              </div>
            );
          })}
        </>
      )}

      {/* ── MATERIALS LIST ── */}
      {materials.length > 0 && (
        <>
          <div style={{ pageBreakBefore: 'always' }} />
          <SectionHead title={`Materials List — ${fmt(materialsTotal)} estimated`} color="#e67e22" />
          {materials.map((group, gi) => (
            <div key={gi} style={{ marginBottom: 14, pageBreakInside: 'avoid' }}>
              <div style={{ fontWeight: 700, fontSize: 12, color: group.color || BRAND,
                borderLeft: `3px solid ${group.color || BRAND}`, paddingLeft: 8, marginBottom: 4 }}>
                {group.title}
                {group.items.reduce((s, i) => s + (i.cost || 0), 0) > 0 &&
                  <span style={{ marginLeft: 8, color: GRAY, fontWeight: 400 }}>
                    {fmt(group.items.reduce((s, i) => s + (i.cost || 0), 0))}
                  </span>}
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <thead>
                  <tr style={{ background: LIGHT_BG }}>
                    <th style={{ textAlign: 'left',  padding: '3px 8px', color: GRAY, borderBottom: `1px solid ${BORDER}` }}>Material</th>
                    <th style={{ textAlign: 'right', padding: '3px 8px', color: GRAY, borderBottom: `1px solid ${BORDER}` }}>Qty</th>
                    <th style={{ textAlign: 'right', padding: '3px 8px', color: GRAY, borderBottom: `1px solid ${BORDER}` }}>Est. Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {group.items.map((item, ii) => (
                    <tr key={ii} style={{ borderBottom: `1px solid ${BORDER}` }}>
                      <td style={{ padding: '3px 8px' }}>{item.label}</td>
                      <td style={{ padding: '3px 8px', textAlign: 'right', color: GRAY }}>{item.qty}</td>
                      <td style={{ padding: '3px 8px', textAlign: 'right', fontWeight: 500 }}>{item.cost ? fmt(item.cost) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </>
      )}

      {/* ── SOW COST SUMMARY ── */}
      <div style={{ pageBreakBefore: 'auto', marginTop: 24 }}>
        <SectionHead title="SOW Cost Summary" color={BRAND_DARK} />
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: LIGHT_BG }}>
              <th style={{ textAlign: 'left',  padding: '5px 8px', borderBottom: `1px solid ${BORDER}`, color: GRAY }}>Section</th>
              <th style={{ textAlign: 'right', padding: '5px 8px', borderBottom: `1px solid ${BORDER}`, color: GRAY }}>Budgeted</th>
              <th style={{ textAlign: 'right', padding: '5px 8px', borderBottom: `1px solid ${BORDER}`, color: GRAY }}>Actual</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['Demo & Disposal',     demoRows],
              ['Major Systems',       systemRows],
              ['Exterior',            exteriorRows],
              ['Landscape',           landscapeRows],
              ...rooms.map(r => [`Room: ${r.name}`, getRoomRows(r.id)]),
            ].filter(([, rows]) => rows.length > 0).map(([sec, rows], i) => {
              const bud = rows.reduce((s, r) => s + (r.budgeted || r.estimated || 0), 0);
              const act = rows.reduce((s, r) => s + (r.actual || 0), 0);
              return (
                <tr key={i} style={{ borderBottom: `1px solid ${BORDER}` }}>
                  <td style={{ padding: '4px 8px' }}>{sec}</td>
                  <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 500 }}>{fmt(bud)}</td>
                  <td style={{ padding: '4px 8px', textAlign: 'right', color: act > 0 ? '#333' : GRAY }}>{act > 0 ? fmt(act) : '—'}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ background: LIGHT_BG, fontWeight: 700, fontSize: 13 }}>
              <td style={{ padding: '6px 8px', borderTop: `2px solid ${BORDER}` }}>Total Rehab (before contingency)</td>
              <td style={{ padding: '6px 8px', textAlign: 'right', borderTop: `2px solid ${BORDER}`, color: BRAND }}>{fmt(grandTotal)}</td>
              <td style={{ padding: '6px 8px', textAlign: 'right', borderTop: `2px solid ${BORDER}`, color: GRAY }}>
                {sowDb.reduce((s, r) => s + (r.actual || 0), 0) > 0 ? fmt(sowDb.reduce((s, r) => s + (r.actual || 0), 0)) : '—'}
              </td>
            </tr>
            <tr style={{ fontWeight: 700, fontSize: 13 }}>
              <td style={{ padding: '4px 8px', color: GRAY }}>Contingency ({deal.contingency_pct || 10}%)</td>
              <td style={{ padding: '4px 8px', textAlign: 'right', color: GRAY }}>{fmt(pl.contingency)}</td>
              <td />
            </tr>
            <tr style={{ background: BRAND, color: '#fff', fontWeight: 800, fontSize: 14 }}>
              <td style={{ padding: '7px 10px', borderRadius: '0 0 0 4px' }}>Total Rehab Budget</td>
              <td style={{ padding: '7px 10px', textAlign: 'right' }}>{fmt(pl.rehabTotal)}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>

      {/* ── P&L SNAPSHOT (full mode only) ── */}
      {!sowOnly && <div style={{ marginTop: 20, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div style={{ background: LIGHT_BG, borderRadius: 6, padding: '12px 16px' }}>
          <div style={{ fontWeight: 700, fontSize: 12, color: GRAY, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>P&amp;L Snapshot</div>
          {[
            ['ARV / Sale Price',   fmt(pl.arv)],
            ['Purchase Price',     fmt(pl.purchasePrice)],
            ['Front-End Costs',    fmt(pl.feBudgeted)],
            ['Rehab Total',        fmt(pl.rehabTotal)],
            ['Holding Costs',      fmt(pl.hcBudgeted)],
            ['Back-End Costs',     fmt(pl.beBudgeted)],
            ['Net Proceeds',       fmt(pl.netBudgeted), pl.netBudgeted >= 0 ? GREEN : '#d63031'],
          ].map(([k, v, color]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: `1px solid ${BORDER}`, fontSize: 12 }}>
              <span style={{ color: GRAY }}>{k}</span>
              <span style={{ fontWeight: 500, color: color || '#1e1e1e' }}>{v}</span>
            </div>
          ))}
        </div>
        <div style={{ background: LIGHT_BG, borderRadius: 6, padding: '12px 16px' }}>
          <div style={{ fontWeight: 700, fontSize: 12, color: GRAY, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Key Metrics</div>
          {[
            ['Profit % of ARV',    pct(pl.profitPctBudgeted), pl.profitPctBudgeted >= (deal.profit_pct || 20) ? GREEN : '#d63031'],
            ['ROI',                pct(pl.roiBudgeted)],
            ['MAO',                fmt(pl.mao)],
            ['HM Loan',            fmt(pl.loan)],
            ['Cash to Close',      fmt(pl.cashToClose)],
            ['Cash on Cash Return',pct(pl.cocReturn)],
          ].map(([k, v, color]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: `1px solid ${BORDER}`, fontSize: 12 }}>
              <span style={{ color: GRAY }}>{k}</span>
              <span style={{ fontWeight: 500, color: color || '#1e1e1e' }}>{v}</span>
            </div>
          ))}
        </div>
      </div>}

      {/* ── Drive folder link ── */}
      {deal.drive_folder_link && (
        <div style={{ marginTop: 16, padding: '10px 14px', background: LIGHT_BG, borderRadius: 6,
          display: 'flex', alignItems: 'center', gap: 10, fontSize: 12 }}>
          <span style={{ fontSize: 18 }}>📁</span>
          <div style={{ flex: 1 }}>
            <strong>Property Photos</strong> — all documentation photos are on Google Drive
          </div>
          <a href={deal.drive_folder_link} target="_blank" rel="noreferrer"
            style={{ color: BRAND, fontWeight: 600, fontSize: 12 }}>
            View Photos ↗
          </a>
        </div>
      )}

      {/* ── Footer ── */}
      <div style={{ marginTop: 20, paddingTop: 12, borderTop: `1px solid ${BORDER}`, display: 'flex', justifyContent: 'space-between', fontSize: 10, color: GRAY }}>
        <span>My Fair Market Offer · Jacksonville, FL · 904-773-CASH</span>
        <span>Estimates only — verify costs with licensed contractors before purchase</span>
      </div>

      {/* ── Print styles ── */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { margin: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          @page { margin: 18mm 14mm; size: letter; }
        }
        body { font-family: Inter, Arial, sans-serif; }
      `}</style>
    </div>
  );
}

// ── Materials list builder (mirrors MaterialsSection logic) ───────────────────

function buildMaterialsList(deal) {
  const groups = [];
  const sys  = deal.sow_systems   || {};
  const ext  = deal.sow_exterior  || {};
  const land = deal.sow_landscape || {};
  const rooms = deal.rooms || [];

  const flooringByType = {};
  for (const room of rooms) {
    if (room.flooring_condition !== 'replace') continue;
    const sqft = room.sq_ft || (room.length_ft && room.width_ft ? room.length_ft * room.width_ft : 0);
    if (!sqft) continue;
    const type = room.flooring_type || 'LVP';
    flooringByType[type] = (flooringByType[type] || 0) + sqft;
  }
  const flooringItems = Object.entries(flooringByType).map(([type, sqft]) => {
    const rates = { LVP: 3.50, carpet: 2.50, tile: 5.00, hardwood: 8.00 };
    return { label: `${type} Flooring`, qty: `${Math.ceil(sqft * 1.1)} sqft (incl. 10% waste)`, cost: Math.round(sqft * (rates[type] || 3.50)) };
  });
  if (flooringItems.length) groups.push({ title: 'Flooring', color: '#e67e22', items: flooringItems });

  let totalWallSqft = 0, totalCeilingSqft = 0;
  for (const room of rooms) {
    const sqft = room.sq_ft || (room.length_ft && room.width_ft ? room.length_ft * room.width_ft : 0);
    if (room.paint_walls && sqft)   totalWallSqft    += Math.sqrt(sqft) * 4 * (room.ceiling_height || 8);
    if (room.paint_ceiling && sqft) totalCeilingSqft += sqft;
  }
  const paintItems = [];
  if (totalWallSqft    > 0) { const g = Math.ceil(totalWallSqft    / 400); paintItems.push({ label: 'Interior Wall Paint',  qty: `~${g} gal`, cost: g * 45 }); }
  if (totalCeilingSqft > 0) { const g = Math.ceil(totalCeilingSqft / 400); paintItems.push({ label: 'Ceiling Paint',         qty: `~${g} gal`, cost: g * 35 }); }
  if (paintItems.length) groups.push({ title: 'Paint', color: '#f39c12', items: paintItems });

  let totalFixtures = 0, totalFans = 0;
  for (const room of rooms) { totalFixtures += room.light_fixtures || 0; totalFans += room.ceiling_fans || 0; }
  const elecItems = [];
  if (totalFixtures > 0) elecItems.push({ label: 'Light Fixtures', qty: totalFixtures, cost: totalFixtures * 150 });
  if (totalFans     > 0) elecItems.push({ label: 'Ceiling Fans',   qty: totalFans,     cost: totalFans     * 200 });
  if (elecItems.length) groups.push({ title: 'Electrical Fixtures', color: '#f1c40f', items: elecItems });

  const kitchenRooms = rooms.filter(r => r.room_type === 'kitchen');
  const kitchenItems = [];
  for (const r of kitchenRooms) {
    if (r.countertop_type && r.countertop_sq_ft) kitchenItems.push({ label: `${label(r.countertop_type)} Countertop`, qty: `${r.countertop_sq_ft} sqft`, cost: null });
    if (r.sink_condition === 'replace') kitchenItems.push({ label: 'Kitchen Sink', qty: 1, cost: 200 });
    const apps = { appliance_fridge:['Refrigerator',800], appliance_stove:['Stove/Range',500], appliance_dishwasher:['Dishwasher',400], appliance_microwave:['Microwave',200], appliance_disposal:['Garbage Disposal',150] };
    for (const [field, [lbl, cost]] of Object.entries(apps)) {
      if (r[field] === 'replace' || r[field] === 'new') kitchenItems.push({ label: lbl, qty: 1, cost });
    }
  }
  if (kitchenItems.length) groups.push({ title: 'Kitchen', color: '#e74c3c', items: kitchenItems });

  const bathRooms = rooms.filter(r => ['bathroom','master_bath'].includes(r.room_type));
  const bathItems = [];
  for (const r of bathRooms) {
    if (r.toilet_condition === 'replace') bathItems.push({ label: `Toilet (${r.name})`, qty: 1, cost: 200 });
    if (r.vanity_condition === 'replace') bathItems.push({ label: `Vanity — ${r.vanity_size || 'single'} (${r.name})`, qty: 1, cost: r.vanity_size === 'double' ? 900 : 600 });
    if (r.tub_shower_condition === 'replace') bathItems.push({ label: `Tub/Shower Unit (${r.name})`, qty: 1, cost: 1500 });
    if (r.tile_sq_ft > 0 && r.tub_shower_condition === 'retile') bathItems.push({ label: `Shower Tile (${r.name})`, qty: `${r.tile_sq_ft} sqft`, cost: Math.round(r.tile_sq_ft * 3) });
  }
  if (bathItems.length) groups.push({ title: 'Bathroom', color: '#2493b5', items: bathItems });

  if (sys.roof_action === 'replace' && sys.roof_sq_ft) {
    const squares = Math.ceil(sys.roof_sq_ft / 100);
    groups.push({ title: 'Roofing', color: '#7f8c8d', items: [
      { label: `Roofing Shingles — ${sys.roof_type || 'shingle'}`, qty: `${squares} squares`, cost: null },
      { label: 'Roofing Underlayment',                              qty: `${squares} squares`, cost: null },
    ]});
  }
  if (sys.hvac_action === 'replace') {
    groups.push({ title: 'HVAC', color: '#16a085', items: [
      { label: `AC Unit — ${sys.hvac_tons || '?'} ton`, qty: 1, cost: null },
      { label: 'Air Handler / Furnace',                  qty: 1, cost: null },
    ]});
  }

  const extItems = [];
  if (ext.window_action && ext.window_action !== 'keep' && ext.window_count > 0) {
    const count = ext.window_action === 'replace_partial' ? Math.ceil(ext.window_count / 2) : ext.window_count;
    extItems.push({ label: `Windows — ${label(ext.window_type || 'double pane')}`, qty: count, cost: count * 350 });
  }
  if (ext.gutters_action === 'replace' && ext.gutters_linear_ft) {
    extItems.push({ label: 'Gutters', qty: `${ext.gutters_linear_ft} lft`, cost: Math.round(ext.gutters_linear_ft * 8) });
  }
  if (extItems.length) groups.push({ title: 'Exterior', color: '#27ae60', items: extItems });

  const landItems = [];
  if (land.sod && land.sod_sq_ft) landItems.push({ label: 'Sod', qty: `${land.sod_sq_ft} sqft`, cost: Math.round(land.sod_sq_ft * 0.45) });
  if (land.fence_action && land.fence_action !== 'none' && land.fence_linear_ft)
    landItems.push({ label: `${label(land.fence_type || 'Wood')} Fence`, qty: `${land.fence_linear_ft} lft`, cost: null });
  if (landItems.length) groups.push({ title: 'Landscape & Hardscape', color: '#39b54a', items: landItems });

  return groups;
}
